import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useQuery } from '@apollo/client';

import {
  SET_LOADING_CUSTOMER,
  SET_ERROR_CUSTOMER,
  usePurchasePage,
  SET_PAYMENT_METHOD,
} from '../../hooks/usePurchasePage';
import LoadingSpinner from '../LoadingSpinner';
import ErrorText from '../ErrorText';
import Button from '../inputs/Button';
import SelectInput from '../inputs/SelectInput';
import RadioInput from '../inputs/RadioInput';
import CreditCard from '../CreditCard';
import AddPaymentMethod from './AddPaymentMethod';
import CheckPaymentMethodStatus from './CheckPaymentMethodStatus';
import { capitalize } from '../../utils';
import { CUSTOMER_QUERY } from '../../queries';
import LoadingOverlay from '../LoadingOverlay';
import PromoCodeForSubscriptions from './PromoCodeForSubscriptions';
import { currency } from '../../utils';
import { isStripePaymentRequired } from '../../utils/featureFlags';

const STRIPE_PK = import.meta.env.VITE_STRIPE_PK;
const stripePromise = loadStripe(STRIPE_PK);

function Payment() {
  const { state, dispatch } = usePurchasePage();
  const { paymentMethod, paymentMethodType, orderDetails } = state;

  const { loading, error, data } = useQuery(CUSTOMER_QUERY, {
    skip: !isStripePaymentRequired(),
  });
  const customer = data?.customer;
  const paymentMethods = customer?.paymentMethods;

  if (!isStripePaymentRequired()) {
    return (
      <>
        {/*<h4>Payment</h4>*/}
        {/*<p className="text-gray-600 py-4">Order based payment</p>*/}
      </>
    );
  }

  useEffect(() => {
    if (paymentMethods?.length > 0) {
      const defaultPaymentMethod = paymentMethods.find(
        ({ card }) => card?.isDefault
      );

      dispatch({
        type: SET_PAYMENT_METHOD,
        payload: {
          paymentMethod: defaultPaymentMethod || paymentMethods[0],
          paymentMethodType: 'saved',
        },
      });
    }
  }, [paymentMethods, dispatch]);

  useEffect(() => {
    dispatch({
      type: SET_LOADING_CUSTOMER,
      payload: loading,
    });
  }, [loading, dispatch]);

  useEffect(() => {
    dispatch({
      type: SET_ERROR_CUSTOMER,
      payload: error,
    });
  }, [error, dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('addPaymentMethod') === 'confirming') {
      dispatch({
        type: SET_PAYMENT_METHOD,
        payload: {
          paymentMethodType: 'saved',
        },
      });
    }
  }, [dispatch]);

  return (
    <>
      <h4>Payment</h4>
      {!error &&
        customer &&
        customer?.paymentMethods?.length > 0 &&
        paymentMethodType === 'saved' && (
          <CreditCard
            className="mr-6"
            last4={paymentMethod?.card?.last4}
            exp={`${paymentMethod?.card?.exp_month} / ${paymentMethod?.card?.exp_year}`}
            brand={paymentMethod?.card?.brand}
            active={true}
            isDefault={paymentMethod?.card?.isDefault}
          />
        )}
      {((error && error.message === 'stripe-customer-does-not-exist') ||
        customer?.paymentMethods?.length === 0 ||
        paymentMethodType === 'new') && (
        <div className="flex flex-col py-10">
          {error && error.message === 'stripe-customer-does-not-exist' && (
            <p>
              You haven't set up payments yet. Please add a payment method
              below.
            </p>
          )}
          <AddPaymentMethod
            className="pt-4"
            urlParamsToPersist={orderDetails}
          />
        </div>
      )}
      <div className="flex flex-col pt-10 pb-2 space-y-3">
        <div className="flex flex-row items-center">
          <button
            className="flex flex-row"
            type="button"
            onClick={() =>
              dispatch({
                type: SET_PAYMENT_METHOD,
                payload: {
                  paymentMethodType: 'saved',
                },
              })
            }
          >
            <RadioInput
              className="mr-2 cursor-pointer"
              checked={paymentMethodType === 'saved'}
              readOnly
            />{' '}
            Saved payment methods
          </button>
          <SelectInput
            className="ml-3"
            onChange={(value) =>
              dispatch({
                type: SET_PAYMENT_METHOD,
                payload: {
                  paymentMethod: paymentMethods.find(({ id }) => id === value),
                },
              })
            }
            value={paymentMethod?.id}
            placeholder="Select a card"
            options={customer?.paymentMethods.map(({ id, card }) => {
              const { brand, last4, isDefault } = card;
              return {
                name: `${capitalize(brand)} | ${last4}${
                  isDefault ? ' (default)' : ''
                }`,
                value: id,
              };
            })}
          />
        </div>
        <button
          className="flex flex-row"
          type="button"
          onClick={() =>
            dispatch({
              type: SET_PAYMENT_METHOD,
              payload: {
                paymentMethodType: 'new',
              },
            })
          }
        >
          <RadioInput
            className="mr-2 cursor-pointer"
            checked={paymentMethodType === 'new'}
            readOnly
          />{' '}
          New payment method
        </button>
      </div>
    </>
  );
}

export default function PurchasePage({
  productLabel,
  productPricing,
  productUpsell,
  productDetails,
  productCompletePurchase,
  backButton,
}) {
  const { state } = usePurchasePage();
  const {
    loadingCreatePaymentIntent,
    errorCreatePaymentIntent,
    loadingCustomer: loading,
    errorCustomer: error,
    totalPrice,
    totalDiscount,
    orderDetails,
    loadingPreviewSubscriptionDiscount,
  } = state;

  const navigate = useNavigate();

  return (
    <div className="w-full h-full flex flex-col overflow-auto py-3 px-6">
      <h3>Order Summary</h3>
      {productLabel}
      {loading && (
        <LoadingSpinner message="Getting existing payment methods..." />
      )}
      {error && error.message !== 'stripe-customer-does-not-exist' && (
        <ErrorText message={error.message} />
      )}
      {!loading && (
        <div className="flex flex-row py-6">
          <Half>
            <Payment />
            <div className={`py-8 space-y-1 ${!productUpsell && 'pb-8'}`}>
              {productPricing}
            </div>
            {productUpsell && <div className="pb-8">{productUpsell}</div>}
            <div className="flex flex-row pb-7 space-x-2">
              <span className="text-xl font-light">
                Total Price for Order:{' '}
              </span>
              <span className="text-lg font-bold">
                {' '}
                {totalDiscount ? (
                  loadingPreviewSubscriptionDiscount ? (
                    currency(totalPrice)
                  ) : (
                    <>
                      <span className="line-through text-gray-400 mr-1">
                        {currency(totalPrice)}
                      </span>
                      {currency(
                        totalDiscount ? totalPrice - totalDiscount : totalPrice
                      )}
                    </>
                  )
                ) : (
                  currency(totalPrice)
                )}
                {state.isSubscription && (
                  <span className="ml-1 text-sm">
                    /{' '}
                    {state.orderDetails?.recurringInterval === 'quarterly'
                      ? 'quarter'
                      : 'month'}
                  </span>
                )}
              </span>
            </div>
            <div className="mb-10">
              {orderDetails?.plan !== 'free' && !orderDetails.upgrading && (
                <PromoCodeForSubscriptions />
              )}
            </div>
            <div className="flex flex-row space-x-3">
              {backButton}
              {!backButton && (
                <Button
                  type="button"
                  secondary
                  small
                  onClick={() => navigate(-1)}
                  disabled={loadingCreatePaymentIntent}
                >
                  Back
                </Button>
              )}
              {productCompletePurchase}
            </div>
          </Half>
          <Half>
            <>
              {!loadingCreatePaymentIntent &&
                !errorCreatePaymentIntent &&
                productDetails}
              {loadingCreatePaymentIntent && (
                <div className="absolute inset-0 cursor-default flex justify-center items-center z-50">
                  <div className="absolute opacity-50 h-full w-full inset-0 bg-gray-500"></div>
                  <div className="absolute h-full w-full inset-0 flex justify-center">
                    <div className="mx-auto my-auto p-24 bg-white rounded-half shadow-lg">
                      <LoadingSpinner message="Completing your purchase..." />
                    </div>
                  </div>
                </div>
              )}
              {errorCreatePaymentIntent && (
                <ErrorText message={errorCreatePaymentIntent.message} />
              )}
            </>
          </Half>
        </div>
      )}
      {state.isSubscription && state.pollingSubscriptionStatus && (
        <LoadingOverlay message="Validating your subscription..." />
      )}
      {state.fetchingProrationPreview && !state.pollingSubscriptionStatus && (
        <LoadingOverlay message="Fetching proration details..." />
      )}
      <Elements stripe={stripePromise}>
        <CheckPaymentMethodStatus />
      </Elements>
    </div>
  );
}

function Half({ children }) {
  return <div className="flex flex-col flex-1">{children}</div>;
}
